import type { List, BuiltInFilterState, ViewType } from "../types";
import type { DialogContextValue } from "../contexts/DialogContext";
import { useView } from "../contexts/ViewContext";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NavigateFn = (opts: any) => void;

export interface UseTodoAppGlobalShortcutsArgs {
  searchTerm: string;
  activeFilterId: string | undefined;
  hasBuiltInFilters: boolean;
  dialog: DialogContextValue;
  showEditDialog: boolean;
  viewType: ViewType;
  toggleShowCompleted: () => void;
  toggleAllSubtasksExpanded: () => void;
  builtInFilterState: BuiltInFilterState;
  lists: List[];
  isRecording: boolean;
  stopRecording: () => void;
  startRecording: () => void;
  navigate: NavigateFn;
  setSearchTerm: (term: string) => void;
  gmailConnected: boolean;
}

export function useTodoAppGlobalShortcuts({
  searchTerm,
  activeFilterId,
  hasBuiltInFilters,
  dialog,
  showEditDialog,
  viewType,
  toggleShowCompleted,
  toggleAllSubtasksExpanded,
  builtInFilterState,
  lists,
  isRecording,
  stopRecording,
  startRecording,
  navigate,
  setSearchTerm,
  gmailConnected,
}: UseTodoAppGlobalShortcutsArgs): void {
  // Keeps the saved filter on when a shortcut changes a built-in one (#744).
  const { setBuiltInFilterState, clearFilters } = useView();
  const isNoDialogOpen = () => dialog.current === null && !showEditDialog;
  const isBoardView = () => viewType === "board";
  const isBoardOrCalendarView = () =>
    viewType === "board" || viewType === "calendar";

  useKeyboardShortcuts([
    // ── Escape ────────────────────────────────────────────────────────────────
    // Clears active search or filter. Fires even while an input is focused so
    // the user can dismiss search results without leaving the search field.
    {
      key: "Escape",
      allowInInput: true,
      when: () =>
        isNoDialogOpen() &&
        (!!searchTerm.trim() || !!activeFilterId || hasBuiltInFilters),
      run: () => {
        if (searchTerm.trim()) {
          setSearchTerm("");
        } else {
          clearFilters();
        }
      },
    },

    // ── / — open search ───────────────────────────────────────────────────────
    // A bare slash, with no modifier. It stays out of text fields so a slash
    // can still be typed in a title, a note, or the search box itself.
    {
      key: "/",
      run: () => dialog.open("search"),
    },

    // ── ? — show shortcuts help (works even with other dialogs open) ──────────
    {
      key: "?",
      run: () => {
        if (dialog.isOpen("shortcut-hints")) {
          dialog.replace("shortcuts");
        } else {
          dialog.open("shortcuts");
        }
      },
    },

    // ── Alt+S — toggle settings (works even with overlay / shortcuts open) ────
    {
      key: "Alt+S",
      run: () => {
        const isDialogOpen = dialog.current !== null || showEditDialog;
        if (dialog.isOpen("settings")) {
          dialog.close();
        } else if (
          dialog.isOpen("shortcut-hints") ||
          dialog.isOpen("shortcuts")
        ) {
          dialog.replace("settings");
        } else if (!isDialogOpen) {
          dialog.open("settings");
        }
      },
    },

    // ── Plain-key shortcuts — all require: no dialog, no modifier ─────────────

    // "+" / "=" — expand/collapse all subtasks (board only)
    {
      key: "+=",
      when: () => isNoDialogOpen() && isBoardView(),
      run: () => toggleAllSubtasksExpanded(),
    },

    // "n" — new task (any view)
    {
      key: "N",
      when: isNoDialogOpen,
      run: () => {
        dialog.open("add-task", null);
      },
    },

    // "v" — toggle voice recording (any view)
    {
      key: "V",
      when: isNoDialogOpen,
      run: () => {
        if (isRecording) {
          stopRecording();
        } else {
          startRecording();
        }
      },
    },

    // View switchers (any view, no dialog)
    { key: "B", when: isNoDialogOpen, run: () => navigate({ to: "/" }) },
    {
      key: "C",
      when: isNoDialogOpen,
      run: () => navigate({ to: "/calendar" }),
    },
    {
      key: "E",
      when: () => isNoDialogOpen() && gmailConnected,
      run: () => navigate({ to: "/emails" }),
    },

    // "h" — toggle show completed (board only)
    {
      key: "H",
      when: () => isNoDialogOpen() && isBoardView(),
      run: () => toggleShowCompleted(),
    },

    // "9" — cycle the 9–5 (Working hours) filter chip. Works on both the
    // board and the calendar sidebar's filter row, mirroring P/L.
    {
      key: "9",
      when: () => isNoDialogOpen() && isBoardOrCalendarView(),
      run: () => {
        const current = builtInFilterState.workHoursFilter ?? null;
        const next =
          current === null ? "only" : current === "only" ? "exclude" : null;
        setBuiltInFilterState(
          { ...builtInFilterState, workHoursFilter: next },
        );
      },
    },

    // "p" — cycle priority filter (board + calendar sidebar)
    {
      key: "P",
      when: () => isNoDialogOpen() && isBoardOrCalendarView(),
      run: () => {
        const priorityValues = [1, 2, 3];
        const currentPriorities = builtInFilterState.priorities;
        if (currentPriorities.length === 0) {
          setBuiltInFilterState(
            {
              ...builtInFilterState,
              priorities: [priorityValues[0]],
            },
          );
        } else if (currentPriorities.length === 1) {
          const idx = priorityValues.indexOf(currentPriorities[0]);
          if (idx < priorityValues.length - 1) {
            setBuiltInFilterState(
              {
                ...builtInFilterState,
                priorities: [priorityValues[idx + 1]],
              },
            );
          } else {
            setBuiltInFilterState(
              { ...builtInFilterState, priorities: [] },
            );
          }
        } else {
          setBuiltInFilterState(
            { ...builtInFilterState, priorities: [] },
          );
        }
      },
    },

    // "l" — cycle list filter (board + calendar sidebar)
    {
      key: "L",
      when: () => isNoDialogOpen() && isBoardOrCalendarView(),
      run: () => {
        if (lists.length === 0) return;
        const cycle: (string | null)[] = [null, ...lists.map((l) => l.id)];
        const currentLists = builtInFilterState.listIds;
        if (currentLists.length !== 1) {
          setBuiltInFilterState(
            { ...builtInFilterState, listIds: [cycle[0]] },
          );
          return;
        }
        const idx = cycle.findIndex((id) => id === currentLists[0]);
        if (idx === -1 || idx === cycle.length - 1) {
          setBuiltInFilterState(
            { ...builtInFilterState, listIds: [] },
          );
        } else {
          setBuiltInFilterState(
            {
              ...builtInFilterState,
              listIds: [cycle[idx + 1]],
            },
          );
        }
      },
    },
  ]);
}
